import { Component, inject, OnDestroy, OnInit, signal, computed } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router } from '@angular/router';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { BaseDataGridComponent } from '@components/base-data-grid.component';
import { dateFormattingOptions } from '@domain/formatting';
import { ByProductItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { ProductsService } from '@services/products.service';
import { ColDef, RowClickedEvent, themeAlpine, GridApi } from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import Fuse from 'fuse.js';
import { debounceTime, Subscription } from 'rxjs';

@Component({
  selector: 'md-view-byproducts-page',
  imports: [PageHeaderComponent, MatFormFieldModule, MatInputModule, MatIconModule, MatButtonModule, AgGridAngular, ReactiveFormsModule, TranslateModule],
  templateUrl: './view-byproducts-page.component.html',
  styleUrl: './view-byproducts-page.component.scss',
  standalone: true
})
export class ViewByproductsPageComponent extends BaseDataGridComponent implements OnInit, OnDestroy {
  private productsService = inject(ProductsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  private gridApi = signal<GridApi<ByProductItem> | null>(null);
  isLoading = signal(false);
  isExporting = signal(false);
  allProducts = signal<ByProductItem[]>([]);
  displayProducts = signal<ByProductItem[]>([]);

  theme = themeAlpine;
  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<ByProductItem>;

  columnDefs = computed(() => {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      { headerName: getTranslatedHeader('FIELDS.CODE'), field: 'id', sortable: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.DESCRIPTION'), valueGetter: p => p.data?.name[this.lang()], sortable: true, flex: 2 },
      { headerName: getTranslatedHeader('FIELDS.CATEGORY'), field: 'cat', sortable: true, filter: true, flex: 1 },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), field: 'createdBy', sortable: true, filter: true, flex: 2 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 2
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), field: 'updatedBy', sortable: true, filter: true, flex: 2 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 2
      }
    ] as ColDef<ByProductItem>[];
  });

  async ngOnInit() {
    this.isLoading.set(true);
    const products = (await this.productsService.getByProducts()).data;
    this.allProducts.set(products);
    this.displayProducts.set(products);

    this.initFuse();
    this.searchTermSubscription = this.searchInput.valueChanges.pipe(debounceTime(300)).subscribe(term => this.search(term));
    this.isLoading.set(false);
  }

  initFuse() {
    this.fuse = new Fuse(this.allProducts(), {
      keys: ['productCode', `name.${this.lang()}`, `productType.name.${this.lang()}`, 'createdBy.name', 'updatedBy.name'],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;
    if (!term || term.length === 0) {
      this.displayProducts.set(this.allProducts());
      return;
    }
    const results = this.fuse.search(term);
    this.displayProducts.set(results.map(r => r.item as ByProductItem));
  }

  rowOnClick(event: RowClickedEvent<ByProductItem, unknown>) {
    const rowItemId = event.data?.id;
    if (rowItemId) {
      this.router.navigate([rowItemId], { relativeTo: this.route });
    }
  }

  onGridReady(params: { api: GridApi<ByProductItem> }) {
    this.gridApi.set(params.api);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'byproducts', this.isExporting);
  }

  ngOnDestroy() {
    this.searchTermSubscription?.unsubscribe();
  }
}
